/**
 * Role-safe execution projection.
 *
 * Turns a resolved requirement set into a view for one audience. The same
 * snapshot must never tell a worker they can proceed when a coordinator would
 * be told they cannot, and a worker must never learn credential-level detail
 * about a professional or statutory failure owned by someone else.
 *
 * Disposition is always re-derived from decision and authority. The stored
 * `blocking` flag is never read: a forged `blocking: false` on a failed
 * statutory requirement has already opened gates three times in this codebase.
 */

import type { RequirementAuthority } from './authority';
import type { GateStage, RequirementDisposition, ResolvedRequirement } from './decisions';
import { gateStageRank, isSatisfiedDecision, requirementDisposition } from './decisions';

// ---------------------------------------------------------------------------
// Audiences
// ---------------------------------------------------------------------------

export const PROJECTION_AUDIENCES = ['WORKER', 'COORDINATOR'] as const;

export type ProjectionAudience = (typeof PROJECTION_AUDIENCES)[number];

const PROJECTION_AUDIENCE_SET: ReadonlySet<string> = new Set(PROJECTION_AUDIENCES);

export function isProjectionAudience(
  value: string | null | undefined,
): value is ProjectionAudience {
  return typeof value === 'string' && PROJECTION_AUDIENCE_SET.has(value);
}

// ---------------------------------------------------------------------------
// Projected shape
// ---------------------------------------------------------------------------

/**
 * Who must act to clear a non-CLEAR disposition. Authority itself is never
 * exposed on this shape — a worker projection must not leak whether the block
 * was statutory, professional or program-owned.
 */
export type ProjectionRouteTo = 'WORKER' | 'COORDINATOR' | 'SUPERVISOR' | 'NOBODY';

export interface ProjectedRequirement {
  code: string;
  stage: GateStage;
  disposition: RequirementDisposition;
  reasonCode: string;
  /** Who resolves this. */
  routeTo: ProjectionRouteTo;
}

export interface ExecutionProjection {
  audience: ProjectionAudience;
  canProceed: boolean;
  blocking: readonly ProjectedRequirement[];
  awaitingReview: readonly ProjectedRequirement[];
  advisory: readonly ProjectedRequirement[];
}

// ---------------------------------------------------------------------------
// Routing
// ---------------------------------------------------------------------------

/**
 * Authorities whose failures are owned above the worker. A blocking failure
 * under any of these is a coordinator problem, not a frontline fix.
 */
const COORDINATOR_OWNED_AUTHORITIES: ReadonlySet<RequirementAuthority> = new Set([
  'STATUTORY',
  'PROGRAM',
  'PROFESSIONAL_SCOPE',
]);

/**
 * Authorities whose failures a worker can clear themselves (policy, plan or
 * contract detail they can still satisfy at the point of care).
 */
const WORKER_OWNED_AUTHORITIES: ReadonlySet<RequirementAuthority> = new Set([
  'PROVIDER_POLICY',
  'PLAN_CLIENT',
  'SERVICE_CONTRACT',
]);

/**
 * Authorities whose specific reason codes a worker must not see when the
 * disposition is not CLEAR. Credential and statutory detail belongs to the
 * coordinator; the worker only needs to know to contact them.
 */
const WORKER_REDACTED_AUTHORITIES: ReadonlySet<RequirementAuthority> = new Set([
  'PROFESSIONAL_SCOPE',
  'STATUTORY',
]);

const WORKER_REDACTED_REASON = 'contact_coordinator';

function routeTo(
  disposition: RequirementDisposition,
  authority: RequirementAuthority,
): ProjectionRouteTo {
  if (disposition === 'CLEAR') return 'NOBODY';
  if (disposition === 'REVIEW') return 'SUPERVISOR';
  // BLOCK
  if (COORDINATOR_OWNED_AUTHORITIES.has(authority)) return 'COORDINATOR';
  if (WORKER_OWNED_AUTHORITIES.has(authority)) return 'WORKER';
  // OPTIONAL_CAPTURE never produces BLOCK (requirementDisposition forces CLEAR).
  return 'NOBODY';
}

function reasonForAudience(
  requirement: ResolvedRequirement,
  disposition: RequirementDisposition,
  audience: ProjectionAudience,
): string {
  if (
    audience === 'WORKER' &&
    disposition !== 'CLEAR' &&
    WORKER_REDACTED_AUTHORITIES.has(requirement.authority)
  ) {
    return WORKER_REDACTED_REASON;
  }
  return requirement.reasonCode;
}

function projectOne(
  requirement: ResolvedRequirement,
  disposition: RequirementDisposition,
  audience: ProjectionAudience,
): ProjectedRequirement {
  return {
    code: requirement.code,
    stage: requirement.stage,
    disposition,
    reasonCode: reasonForAudience(requirement, disposition, audience),
    routeTo: routeTo(disposition, requirement.authority),
  };
}

// ---------------------------------------------------------------------------
// Projection
// ---------------------------------------------------------------------------

/**
 * Builds a role-safe view of the requirements that apply at or before `stage`.
 *
 * Stage scoping mirrors `evaluateGate`: only requirements whose stage rank is
 * at or before the requested gate are in scope. Disposition is re-derived;
 * `ResolvedRequirement.blocking` is never consulted.
 */
export function projectExecution(
  requirements: readonly ResolvedRequirement[],
  stage: GateStage,
  audience: ProjectionAudience,
): ExecutionProjection {
  const stageRank = gateStageRank(stage);
  const inScope = requirements.filter((r) => gateStageRank(r.stage) <= stageRank);

  const blocking: ProjectedRequirement[] = [];
  const awaitingReview: ProjectedRequirement[] = [];
  const advisory: ProjectedRequirement[] = [];

  for (const requirement of inScope) {
    // Re-derive. Never trust the stored flag.
    const disposition = requirementDisposition(requirement.decision, requirement.authority);

    // Optional capture is advice: it never blocks and never awaits review,
    // even if a deserialised record carries a forged blocking flag.
    if (requirement.authority === 'OPTIONAL_CAPTURE') {
      if (!isSatisfiedDecision(requirement.decision)) {
        advisory.push(projectOne(requirement, disposition, audience));
      }
      continue;
    }

    switch (disposition) {
      case 'BLOCK':
        blocking.push(projectOne(requirement, disposition, audience));
        break;
      case 'REVIEW':
        awaitingReview.push(projectOne(requirement, disposition, audience));
        break;
      case 'CLEAR':
        break;
    }
  }

  return {
    audience,
    canProceed: blocking.length === 0 && awaitingReview.length === 0,
    blocking,
    awaitingReview,
    advisory,
  };
}
